import { useMemo } from 'react'
import { convertToDisplayCurrency } from '../hooks/useDashboardCurrency'
import type { Trade } from '../types/trade'
import DashboardWidgetTitle from './DashboardWidgetTitle'

interface DashboardWidgetTotalPnlOpenProps {
  trades: Trade[]
  selectedCurrency: string
  latestRates: Map<string, number>
}

const cardClass =
  'h-full w-full dashboard-card p-3 md:p-4 flex flex-col justify-center'

export default function DashboardWidgetTotalPnlOpen({
  trades,
  selectedCurrency,
  latestRates,
}: DashboardWidgetTotalPnlOpenProps) {
  const { total, hasData } = useMemo(() => {
    const open = trades.filter((trade) => !trade.exit_date)
    if (open.length === 0) return { total: 0, hasData: false }

    let sum = 0
    let hasAny = false
    for (const tr of open) {
      const up = tr.unrealized_pnl
      if (up == null || Number.isNaN(up)) continue
      hasAny = true
      const curr = tr.currency || 'USD'
      sum += convertToDisplayCurrency(up, curr, selectedCurrency, latestRates)
    }
    return { total: sum, hasData: hasAny }
  }, [trades, selectedCurrency, latestRates])

  const valueClass = !hasData
    ? 'text-gray-900'
    : total >= 0
      ? 'text-green-600'
      : 'text-red-600'

  const valueText = !hasData
    ? '–'
    : `${total >= 0 ? '+' : ''}${total.toFixed(2).replace('.', ',')} ${selectedCurrency}`

  return (
    <div className={cardClass}>
      <div className="flex md:hidden flex-row items-center justify-between gap-2 py-0.5">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider truncate shrink-0">
          <DashboardWidgetTitle widgetId="totalPnlOpen" />
        </p>
        <p className={`text-base font-bold shrink-0 ${valueClass}`}>{valueText}</p>
      </div>
      <div className="hidden md:block">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
          <DashboardWidgetTitle widgetId="totalPnlOpen" />
        </p>
        <p className={`text-xl md:text-2xl font-bold ${valueClass}`}>{valueText}</p>
      </div>
    </div>
  )
}
